import { useEffect, useState } from 'react'
import { getPushState, subscribeToPush, type PushState } from '../lib/push.ts'

/**
 * ПЛАШКА «УВЕДОМЛЕНИЯ ВЫКЛЮЧЕНЫ»
 *
 * Уведомления включались только из меню: «Уведомления» → «Включить».
 * Туда никто не заходит сам — человек ставит приложение и уверен, что
 * сообщения придут, как в любом мессенджере. А они не приходят вовсе:
 * серверу просто некуда их отправить.
 *
 * Так и случилось после пересоздания аккаунта: сообщения писались
 * в колокольчик, а на телефон не уходило ничего, и об этом не знал никто.
 *
 * Теперь над списком переписок висит плашка, пока уведомления на этом
 * устройстве не включены. Её можно отложить, но она вернётся через день:
 * мессенджер без уведомлений — это почта, которую надо проверять руками.
 */

const SNOOZE_KEY = 'push-banner-snoozed-until'
const SNOOZE_MS = 24 * 3600_000

function snoozed(): boolean {
  try {
    return Number(localStorage.getItem(SNOOZE_KEY) ?? 0) > Date.now()
  } catch {
    return false
  }
}

export function PushBanner({ onOpenSettings }: { onOpenSettings: () => void }) {
  const [state, setState] = useState<PushState | null>(null)
  const [hidden, setHidden] = useState(snoozed)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    const check = () =>
      getPushState()
        .then((s) => alive && setState(s))
        .catch(() => {})

    void check()
    // Человек мог включить уведомления в меню — плашка должна уйти сама
    document.addEventListener('visibilitychange', check)
    const timer = window.setInterval(check, 60_000)
    return () => {
      alive = false
      document.removeEventListener('visibilitychange', check)
      window.clearInterval(timer)
    }
  }, [])

  if (hidden || !state) return null
  if (state.kind !== 'available' && state.kind !== 'needs-install' && state.kind !== 'denied') {
    return null
  }

  const snooze = () => {
    try {
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS))
    } catch {
      /* без памяти — просто спрячем до перезапуска */
    }
    setHidden(true)
  }

  const enable = async () => {
    setBusy(true)
    setError(null)
    try {
      await subscribeToPush()
      setState({ kind: 'subscribed' })
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Не получилось')
      void getPushState().then(setState).catch(() => {})
    } finally {
      setBusy(false)
    }
  }

  const text =
    state.kind === 'needs-install'
      ? 'Чтобы сообщения приходили на телефон, добавь приложение на экран «Домой»'
      : state.kind === 'denied'
        ? 'Уведомления запрещены в настройках телефона — сообщения не придут, пока приложение закрыто'
        : 'Уведомления выключены — сообщения не придут, пока приложение закрыто'

  return (
    <div className="push-banner" role="status">
      <div className="push-banner-text">
        {text}
        {error && <span className="push-banner-error">{error}</span>}
      </div>
      <div className="push-banner-actions">
        {state.kind === 'available' ? (
          <button className="push-banner-go" onClick={() => void enable()} disabled={busy}>
            {busy ? '…' : 'Включить'}
          </button>
        ) : (
          <button className="push-banner-go" onClick={onOpenSettings}>
            Как?
          </button>
        )}
        <button className="push-banner-later" onClick={snooze}>
          Позже
        </button>
      </div>
    </div>
  )
}
